import styled from 'styled-components'
import truncate from 'lodash/truncate'
import Media from 'components/utils/Media'

import useIsServer from 'hooks/useIsServer'

import { breakpoints, media } from '../../styles/media'

import LazyImage from '../display/LazyImage'
import Button from '../display/Button'

import MediaWrapper from './shared/MediaWrapper'
import {
  CardWrapper,
  Content,
  Details,
  TitleWrapper,
  Title,
} from './shared/styled'

import ImageOrSlider from './shared/ImageOrSlider'

export const ListingStoryCardWrapper = styled(CardWrapper)`
  border: 1px solid #d1d1d1;
  border-radius: 5px;
  overflow: hidden;

  ${MediaWrapper} {
    border-radius: 0;
  }

  ${media.md`
    ${MediaWrapper} {
      padding-top: 58%;
    }
  `}
`

export const StoryContent = styled(Content)`
  padding: 16px 8px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  background-color: ${({ theme }) => theme.brand.sand};

  ${media.md`
    padding: 32px;
  `}
`

const StoryDetails = styled(Details)`
  flex: 1;
  line-height: 1.6;

  overflow: visible;
  text-overflow: ellipsis;
  white-space: pre-wrap;

  ${media.md`
    padding-right: 32px;
  `}

  p {
    margin-bottom: 32px;
  }
`

const StoryTitleWrapper = styled(TitleWrapper)`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  padding-right: 0;
`

const StoryTitle = styled(Title)`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.brand.jungle};

  ${media.md`
    font-size: 30px;
  `}
`

const Author = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  div {
    display: inline-block;
  }

  ${media.md`
    margin-bottom: 0;
    font-size: 20px;
    flex-direction: column;
    text-align: center;

    div {
      display: block;
    }
  `}
`

const Avatar = styled.div`
  overflow: hidden;
  width: 40px;
  height: 40px;

  background-color: #333;
  border-radius: 50%;

  margin-right: 16px;

  ${LazyImage} {
    border-radius: 50%;
  }

  ${media.md`
    width: 100px;
    height: 100px;
    margin-bottom: 8px;
    margin-right: 0;
  `}
`

const StoryBy = styled.div`
  a {
    color: ${({ theme }) => theme.brand.jungle};
    text-decoration: none;
  }
`

const AuthorName = styled.span`
  color: ${({ theme }) => theme.brand.jungle};
`

const ButtonWrapper = styled.div`
  ${Button} {
    display: inline-block;
    width: 100%;
  }

  ${media.md`
    text-align: center;
    margin-right: -132px;

    ${Button} {
      width: auto;
    }
  `}
`

type Photo = {
  image: R_Image
}

export type Story = {
  id: number
  title: string
  description: string
  url: string
  promo_image: R_Image
  photos: Photo[]
  user: {
    first_name: string
    avatar: string
  }
}

type ListingStoryCardProps = {
  photos: Photo[]
  story: Story
}

const ListingStoryCard = ({ photos, story }: ListingStoryCardProps) => {
  const serverSide = useIsServer()

  const renderAuthor = () => {
    if (serverSide) return null

    return (
      <Author>
        <Avatar>
          <LazyImage
            data-src={story.user.avatar}
            alt={story.user.first_name}
            preload
          />
        </Avatar>
        <StoryBy>
          <div>Story by&nbsp;</div>
          <AuthorName>{story.user.first_name}</AuthorName>
        </StoryBy>
      </Author>
    )
  }

  return (
    <ListingStoryCardWrapper>
      <MediaWrapper>
        <ImageOrSlider title={story.title} photos={photos} />
      </MediaWrapper>

      <Media query={`(min-width: ${breakpoints.md}px)`}>
        {(largerThanMd: boolean) => (
          <StoryContent>
            <StoryDetails>
              <StoryTitleWrapper>
                <StoryTitle title={story.title}>{story.title}</StoryTitle>
              </StoryTitleWrapper>

              {!largerThanMd && renderAuthor()}

              <p>
                {truncate(story.description, {
                  length: 232,
                })}
              </p>

              {!serverSide && (
                <ButtonWrapper>
                  <Button
                    as="a"
                    href={story.url}
                    ghost={!largerThanMd}
                    compact={!largerThanMd}
                  >
                    Discover {story.user.first_name}&apos;s Story
                  </Button>
                </ButtonWrapper>
              )}
            </StoryDetails>

            {largerThanMd && renderAuthor()}
          </StoryContent>
        )}
      </Media>
    </ListingStoryCardWrapper>
  )
}

export default ListingStoryCard
